import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatChipSelectionChange,
  MatChipsModule,
} from '@angular/material/chips';
import {
  MatDatepicker,
  MatDatepickerInputEvent,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { PuebloBase } from '@components/pueblo-base.component';
import { SelectDialogContent } from '@components/select-dialog/select-dialog.component';
import { TimeSelect } from '@components/time-select/time-select.component';
import {
  TransitionView,
  TransitionViewContainer,
} from '@components/transition-view/transition-view.component';
import {
  UserList,
  UserListModel,
} from '@components/user-list/user-list.component';
import {
  makeUnsavedChangesDialogConfig,
  PuebloDialog,
} from '@services/pueblo-dialogs.service';
import { EMPTY_HANG } from '@utils/defaults';
import { timeString } from '@utils/hang-utils';
import { resource } from '@utils/signal-utils';
import { now } from '@utils/time-utils';
import { addHours, isToday, startOfDay } from 'date-fns';
import { Hang, Pueblo, UserResourceName } from 'pueblo-types';
import {
  combineLatest,
  debounceTime,
  first,
  map,
  of,
  skip,
  withLatestFrom,
} from 'rxjs';
import {
  IHangForm,
  ILocationForm,
  InterestOption,
  ITimespan,
  PuebloOption,
} from '../form-types';
import { InterestSelect } from '../interest-select/interest-select.component';
import { SubOption, SubOptionForm } from '../options/suboption-form.component';
import { PuebloForm } from '../pueblo-form/pueblo-form.component';
import { PuebloSelect } from '../pueblo-select/pueblo-select.component';

enum View {
  DETAILS = 'details',
  INTERESTS_AND_PUEBLOS = 'interests-and-pueblos',
  NEW_PUEBLO = 'new-pueblo',
}

enum NotifyPuebloOption {
  EVERYONE = 'everyone',
  INTERESTS = 'interests',
}

@Component({
  selector: 'new-hang-form',
  templateUrl: 'new-hang-form.component.html',
  styleUrl: 'new-hang-form.component.scss',
  imports: [
    InterestSelect,
    MatButtonModule,
    MatChipsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatRadioModule,
    MatSelectModule,
    PuebloForm,
    PuebloSelect,
    ReactiveFormsModule,
    SubOptionForm,
    TimeSelect,
    TransitionViewContainer,
    TransitionView,
    UserList,
  ],
  providers: [{ provide: SelectDialogContent, useExisting: NewHangForm }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewHangForm extends PuebloBase {
  private readonly fb = inject(NonNullableFormBuilder);

  protected readonly View = View;
  protected readonly NotifyPuebloOption = NotifyPuebloOption;
  protected readonly SubOption = SubOption;
  protected Visibility = Hang.Visibility;

  protected readonly $newPuebloForm =
    viewChild.required<PuebloForm>('newPuebloForm');

  protected readonly $timeInput =
    viewChild.required<ElementRef<HTMLInputElement>>('timeInput');

  protected readonly $currentView = signal(View.DETAILS);
  protected readonly views = [
    View.DETAILS,
    View.INTERESTS_AND_PUEBLOS,
    View.NEW_PUEBLO,
  ];

  protected readonly $showInterestsSelect = signal(false);
  protected readonly $interestFilter = signal('');

  protected readonly minDate = startOfDay(new Date());

  readonly $hang = input<Hang>(EMPTY_HANG, {
    alias: 'hang',
  });
  protected readonly created = output<Hang>();
  protected readonly canceled = output();

  protected readonly selectedPueblosResource = resource({
    request: () => ({ pueblosChanges: this.$pueblosChanges() }),
    loader: ({ request }) =>
      this.httpStore.pueblos$(
        request.pueblosChanges?.map((option) => option.pueblo.resourceName) ??
          [],
      ),
  });

  protected readonly selectedInterestsResource = resource({
    request: () => ({ interestsChanges: this.$interestsChanges() }),
    loader: ({ request }) =>
      of(request.interestsChanges?.map((option) => option.interest)),
  });

  private readonly $nextShortUrl = toSignal(this.httpStore.nextHangShortUrl$());

  protected readonly form = this.fb.group<IHangForm>({
    title: this.fb.control(''),
    emoji: this.fb.control(''),
    location: this.fb.group<ILocationForm>({
      locationName: this.fb.control(''),
      locationAddress: this.fb.control(''),
      latitude: this.fb.control(0),
      longitude: this.fb.control(0),
    }),
    time: this.fb.control<ITimespan>({
      start: now(),
      end: addHours(now(), 1),
    }),
    pueblos: this.fb.group({
      selectedPueblos: this.fb.control([] as ReadonlyArray<PuebloOption>),
    }),
    interests: this.fb.group({
      selectedInterests: this.fb.control([] as ReadonlyArray<InterestOption>),
    }),
    details: this.fb.control(''),
    link: this.fb.control(''),
    visibility: this.fb.control<Hang.Visibility>(Hang.Visibility.PUBLIC),
  });

  protected readonly $formChanges = toSignal(this.form.valueChanges);
  protected readonly $titleChanges = toSignal(
    this.form.controls.title.valueChanges.pipe(debounceTime(1000)),
    { initialValue: '' },
  );
  protected readonly $timeChanges = toSignal(
    this.form.controls.time.valueChanges,
    { initialValue: { start: now(), end: addHours(now(), 1) } },
  );
  protected readonly $pueblosChanges = toSignal(
    this.form.controls.pueblos.valueChanges.pipe(
      map((options) => options.selectedPueblos),
    ),
    { initialValue: [] },
  );
  protected readonly $interestsChanges = toSignal(
    this.form.controls.interests.valueChanges.pipe(
      map((options) => options.selectedInterests),
    ),
    { initialValue: [] },
  );

  protected readonly $usersToNotify = signal<ReadonlyArray<UserListModel>>([]);

  protected readonly $puebloMembers = computed(() => [
    ...new Set(
      this.$pueblosChanges()
        ?.map((option) => option.pueblo.members)
        .flatMap((members) => members)
        .filter((member) => member !== this.$appUser().resourceName),
    ),
  ]);

  protected readonly $showInterestCountBadge = computed(
    () =>
      this.$pueblosChanges()?.some(
        (option) => option.notify === NotifyPuebloOption.INTERESTS,
      ) ?? false,
  );

  protected readonly $isFormDirty = toSignal(
    this.form.valueChanges.pipe(
      skip(1),
      map(() => true),
    ),
    { initialValue: false },
  );

  protected readonly $isNextDisabled = computed(() => {
    switch (this.$currentView()) {
      case View.DETAILS:
        return (
          !this.$formChanges()?.title ||
          !this.$formChanges()?.location?.locationName
        );
      case View.NEW_PUEBLO:
        return false;
      case View.INTERESTS_AND_PUEBLOS:
        return !this.$pueblosChanges()?.length;
      default:
    }
  });

  pbEffects(): void {
    effect(() => {
      this.$timeInput().nativeElement.value = timeString(
        this.$timeChanges()?.start ?? new Date(),
        'upper',
      );
    });

    effect(() => {
      combineLatest([
        this.httpStore.pueblos$(this.$hang().pueblos),
        this.httpStore.interests$(this.$hang().interests),
      ])
        .pipe(first())
        .subscribe(([pueblos, interests]) => {
          const hang = this.$hang();
          untracked(() => {
            this.form.setValue({
              title: hang.title,
              emoji: hang.emoji,
              location: {
                locationName: hang.location?.displayName ?? '',
                locationAddress: hang.location?.address ?? '',
                latitude: hang.location?.coords?.lat ?? 0,
                longitude: hang.location?.coords?.lng ?? 0,
              },
              time: {
                start: hang.start,
                end: hang.end,
              },
              pueblos: {
                selectedPueblos: pueblos.map((pueblo) => ({
                  pueblo,
                  selected: true,
                  notify: NotifyPuebloOption.EVERYONE,
                })),
              },
              interests: {
                selectedInterests: interests.map((interest) => ({
                  interest,
                  selected: true,
                  userCount: interest.users.length,
                })),
              },
              details: hang.details ?? '',
              link: hang.url ?? '',
              visibility: hang.visibility ?? Hang.Visibility.PUBLIC,
            });
          });
        });
    });

    effect(() => {
      const pueblos = this.$pueblosChanges() ?? [];
      const interests = this.$interestsChanges() ?? [];

      let usersFromPueblos: UserResourceName[] = [];
      pueblos.forEach(({ pueblo, notify }) => {
        if (notify === NotifyPuebloOption.EVERYONE) {
          usersFromPueblos = usersFromPueblos.concat(
            pueblo.members.filter(
              (member) => member !== this.$appUser().resourceName,
            ) ?? [],
          );
        } else if (notify === NotifyPuebloOption.INTERESTS) {
          usersFromPueblos = usersFromPueblos.concat(
            interests
              .map((option) => option.interest.users)
              .flat()
              .filter(
                (user) =>
                  pueblos
                    .map((option) => option.pueblo.members)
                    .flat()
                    .includes(user) && this.$appUser().resourceName !== user,
              )
              .sort((a, b) => a.localeCompare(b)),
          );
        }
      });

      this.httpStore
        .users$([...new Set(usersFromPueblos)])
        .pipe(
          first(),
          map((users) => users.map((user) => ({ user: user }))),
        )
        .subscribe((users) => {
          this.$usersToNotify.set(users);
        });
    });

    effect(() => {
      if (!this.$interestsChanges()?.length) {
        untracked(() => {
          this.form.controls.pueblos.setValue({
            selectedPueblos: (this.$pueblosChanges() ?? []).map((option) => ({
              ...option,
              notify: NotifyPuebloOption.EVERYONE,
            })),
          });
        });
      }
    });

    effect(() => {
      if (!this.$titleChanges()) return;
      this.pbClient
        .generateInterestSuggestions(this.$titleChanges())
        .pipe(first(), withLatestFrom(this.pbClient.listAllInterests()))
        .subscribe(([generatedInterests, allInterests]) => {
          this.form.controls.interests.setValue({
            selectedInterests: allInterests
              .filter((interest) => generatedInterests.includes(interest.name))
              .map((interest) => ({
                interest,
                selected: true,
                userCount: 0,
              })),
          });
        });
    });
  }

  protected next() {
    switch (this.$currentView()) {
      case View.DETAILS:
        this.$currentView.set(View.INTERESTS_AND_PUEBLOS);
        break;
      case View.INTERESTS_AND_PUEBLOS:
        this.submit();
        break;
      case View.NEW_PUEBLO:
        this.$newPuebloForm()?.submit();
        break;
      default:
    }
  }

  protected back() {
    switch (this.$currentView()) {
      case View.DETAILS:
        if (this.$isFormDirty()) {
          this.pbDialogs.open(
            PuebloDialog.SIMPLE,
            makeUnsavedChangesDialogConfig(() => this.canceled.emit()),
          );
        } else {
          this.canceled.emit();
        }
        break;
      case View.INTERESTS_AND_PUEBLOS:
        this.$currentView.set(View.DETAILS);
        break;
      case View.NEW_PUEBLO:
        if (this.$newPuebloForm().isDirty()) {
          this.pbDialogs.open(
            PuebloDialog.SIMPLE,
            makeUnsavedChangesDialogConfig(() => {
              this.$newPuebloForm().reset();
              this.$currentView.set(View.INTERESTS_AND_PUEBLOS);
            }),
          );
        } else {
          this.$currentView.set(View.INTERESTS_AND_PUEBLOS);
        }
        break;
      default:
    }
  }

  protected setView(view: View) {
    this.$currentView.set(view);
  }

  private submit() {
    if (!this.form.valid) return;

    const hang: Hang = {
      ...EMPTY_HANG,
      title: this.form.controls.title.value,
      host: this.$appUser().resourceName,
      emoji: this.form.controls.emoji.value,
      location: {
        displayName: this.form.controls.location.value.locationName ?? '',
        address: this.form.controls.location.value.locationAddress ?? '',
        coords: {
          lat: this.form.controls.location.value.latitude ?? 0,
          lng: this.form.controls.location.value.longitude ?? 0,
        },
      },
      start: this.form.controls.time.value.start,
      end: this.form.controls.time.value.end,
      url: this.form.controls.link.value,
      details: this.form.controls.details.value,
      pueblos:
        this.form.controls.pueblos.value.selectedPueblos?.map(
          (option) => option.pueblo.resourceName,
        ) ?? [],
      interests:
        this.form.controls.interests.value.selectedInterests?.map(
          (option) => option.interest.resourceName,
        ) ?? [],
      sharedWithUsers: this.$usersToNotify().map(
        (userListModel) => userListModel.user.resourceName,
      ),
      shortUrl: this.$nextShortUrl() || '',
    };

    this.httpStore.createHang(hang, {
      onSuccess: (createdHang: Hang) => this.created.emit(createdHang),
    });
  }

  protected openDatePicker(datepicker: MatDatepicker<Date>) {
    datepicker.open();
  }

  protected changeDate(event: MatDatepickerInputEvent<Date>) {
    if (!this.form) return;
    const newDate = startOfDay(event.value ?? new Date());

    const currDate = startOfDay(this.form.controls.time.value.start);
    const offsetMs = newDate.getTime() - currDate.getTime();
    const prevStart = this.form.controls.time.value.start;
    const prevEnd = this.form.controls.time.value.end;
    let newStart = new Date(prevStart.getTime() + offsetMs);
    if (isToday(newStart) && newStart < new Date()) {
      newStart = new Date();
    }
    const newEnd = new Date(prevEnd.getTime() + offsetMs);
    this.form.controls.time.setValue({ start: newStart, end: newEnd });
  }

  protected puebloCreated(pueblo: Pueblo) {
    this.$newPuebloForm().reset();
    this.$currentView.set(View.INTERESTS_AND_PUEBLOS);
    this.form.controls.pueblos.setValue({
      selectedPueblos: [
        ...(this.$pueblosChanges() ?? []),
        {
          pueblo: pueblo,
          selected: true,
          notify: NotifyPuebloOption.EVERYONE,
        },
      ],
    });
  }

  protected publicVisibilitySelected(change: MatChipSelectionChange): void {
    change.source.selected = true;
  }

  protected privateVisibilitySelected(change: MatChipSelectionChange): void {
    change.source.selected = true;
  }
}
